import {
  createContext,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import {
  Asset,
  AssetFilter,
  AssetOrder,
  InputMaybe,
  Sort,
} from "../generated/gql/graphql";
import useAssets from "../hooks/query/asset/use-assets";
import useAssetFilter from "../hooks/useAssetFilter";
import { ConfirmationModalType } from "../types/confirm-modal";
import { parseType } from "../utils/graphql";
import { emptyFn } from "../utils/provider";

import AssetListProvider from "./asset-list-provider";

type FilesProviderContextType = {
  page: number;
  setPage: (page: number) => void;
  totalCount: number;
  setTotalCount: (count: number) => void;
  confirmationModal: ConfirmationModalType;
  setConfirmationModal: (val: ConfirmationModalType) => void;
  limit: number;
  setLimit: (limit: number) => void;
  offset: number;
  setOffset: (offset: number) => void;
  filter: InputMaybe<AssetFilter> | undefined;
  setFilter: (filter: InputMaybe<AssetFilter>) => void;
  orderBy?: AssetOrder;
  setOrderBy: (orderBy: AssetOrder) => void;
  assetsChanged: () => void;

  isLoading: boolean;
  assets: Asset[];
  isError: boolean;
};

export const FilesContext = createContext<FilesProviderContextType>({
  page: 0,
  setPage: emptyFn,
  totalCount: 0,
  setTotalCount: emptyFn,
  confirmationModal: undefined,
  setConfirmationModal: emptyFn,
  limit: 0,
  setLimit: emptyFn,
  offset: 0,
  setOffset: emptyFn,
  filter: undefined,
  setFilter: emptyFn,
  setOrderBy: emptyFn,
  assetsChanged: emptyFn,

  assets: [],
  isLoading: true,
  isError: false,
});

type Props = {
  children: ReactNode | ReactNode[];
};

export default function FilesProvider(props: Props): ReactElement {
  const { children } = props;

  const [searchParams] = useSearchParams();

  const [confirmationModal, setConfirmationModal] = useState<
    ConfirmationModalType | undefined
  >(undefined);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [limit, setLimit] = useState<number>(16);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1"));
  const [orderBy, setOrderBy] = useState<AssetOrder>({
    date_created: Sort.Desc,
  });
  const { filter, setFilter } = useAssetFilter();

  const [assets, setAssets] = useState<Asset[]>([]);
  const { data, loading, error, refetch } = useAssets({
    filter: filter,
    pagination: {
      offset: offset,
      page: page,
      limit: limit,
    },
    orderBy: [
      {
        ...orderBy,
      },
    ],
  });

  function parseData(data: unknown): void {
    if (!data) {
      return;
    }

    const {
      offset: loadedOffset,
      totalCount: loadedTotalCount,
      page: loadedPage,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
    } = data["assets"];

    setOffset(loadedOffset);
    setTotalCount(loadedTotalCount);
    setPage(loadedPage);
    setAssets(parseType<Asset[]>("assets", data));
  }

  useEffect(() => {
    parseData(data);
  }, [data]);

  useEffect(() => {
    refetch({
      filter: filter,
      pagination: {
        offset: offset,
        page: page,
        limit: limit,
      },
      orderBy: [
        {
          ...orderBy,
        },
      ],
    });
  }, [filter, offset, page, limit, orderBy]);

  function applyParams(params: string): void {
    const newUrl = `${window.location.pathname}${
      params !== "" ? `?${params}` : ""
    }`;

    // Note(john): Update the URL without reloading the page
    window.history.replaceState(null, "", newUrl);
  }

  function applyFilterParametersAsUrlQuery(
    pageNumber: number,
    filter: InputMaybe<AssetFilter>
  ): void {
    const params = new URLSearchParams();

    if (filter?.categoryId && filter.categoryId.eq) {
      params.set("categoryId", filter.categoryId.eq);
    }

    if (filter?.name && filter.name.icontains) {
      params.set("name", filter.name.icontains);
    }

    if (filter?.status && filter.status.eq) {
      params.set("status", filter.status.eq);
    }

    if (filter?.assetTypes && filter.assetTypes.eq) {
      params.set("assetTypes", filter.assetTypes.eq);
    }

    if (filter?.approval && filter.approval.eq) {
      params.set("approval", filter.approval.eq);
    }

    if (filter?.metashotsType && filter.metashotsType.eq) {
      params.set("metashotsType", filter.metashotsType.eq);
    }

    if (filter?.languageRegionId && filter.languageRegionId.eq) {
      params.set("languageRegionId", filter.languageRegionId.eq);
    }

    params.set("page", pageNumber.toString());

    applyParams(params.toString());
  }

  function applyParametersToUrlQuery(
    pageNumber: number,
    filter: InputMaybe<AssetFilter> | undefined
  ): void {
    if (!filter) {
      const params = new URLSearchParams();
      params.set("page", pageNumber.toString());
      applyParams(params.toString());
    } else {
      applyFilterParametersAsUrlQuery(pageNumber, filter);
    }
  }

  function internalSetFilter(filter: InputMaybe<AssetFilter>): void {
    // Note(john): Reset here all necessary "result values" (e.g. page).
    setPage(1);

    applyParametersToUrlQuery(page, filter);

    setFilter(filter || undefined);
  }

  function internalSetPage(pageNumber: number): void {
    setPage(pageNumber);
    applyParametersToUrlQuery(pageNumber, filter);
  }

  function internalSetOrder(order: AssetOrder): void {
    setOrderBy(order);

    if (order.name) {
      searchParams.set("order", "name");
    }

    if (order.date_created) {
      searchParams.set("order", "createdAt");
    }

    applyParams(searchParams.toString());
  }

  const values = useMemo(
    () => ({
      totalCount,
      setTotalCount,
      confirmationModal,
      setConfirmationModal,
      limit,
      setLimit,
      offset,
      setOffset,
      page,
      setPage: internalSetPage,
      filter,
      setFilter: internalSetFilter,
      orderBy,
      setOrderBy: internalSetOrder,
      assetsChanged: refetch,

      // Note: Current state and data,
      assets,
      isLoading: loading,
      // ToDo: Add proper error messaging
      isError: error !== undefined,
    }),
    [
      totalCount,
      limit,
      offset,
      page,
      filter,
      orderBy,
      assets,
      loading,
      error,
      confirmationModal,
    ]
  );

  return (
    <FilesContext.Provider value={values}>
      <AssetListProvider initAssets={assets}>{children}</AssetListProvider>
    </FilesContext.Provider>
  );
}
